import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

/**
 * Anexos de um lançamento — RF-ANE-01.
 *
 * ================================================================
 * SÓ APARECE EM LANÇAMENTO JÁ SALVO
 * ================================================================
 *
 * Um anexo se prende a um id, e um lançamento novo ainda não tem. Deixar o
 * campo visível antes de salvar criaria a expectativa de que o arquivo vai
 * junto — e ele seria descartado no envio, sem aviso.
 *
 * Melhor dizer "salve primeiro" do que perder o arquivo em silêncio.
 */

type Anexo = {
  id: number;
  original_name: string;
  extension: string;
  size_human: string;
  description: string | null;
  is_image: boolean;
  is_pdf: boolean;
  uploaded_by: string | null;
  created_at: string;
  /** Falso quando a ficha existe mas o arquivo sumiu do disco. */
  available: boolean;
};

export function AttachmentPanel({
  transactionId,
  entityIds,
}: {
  transactionId: number | null;
  entityIds: number[];
}) {
  const queryClient = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [progresso, setProgresso] = useState<number | null>(null);

  const lista = useQuery({
    queryKey: ['attachments', transactionId],
    queryFn: () =>
      api.get<{ data: Anexo[] }>(`/transactions/${transactionId}/attachments`, { entityIds }),
    enabled: transactionId !== null,
    select: (r) => r.data,
  });

  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/attachments/${id}`, { entityIds }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attachments', transactionId] }),
  });

  async function enviar(arquivo: File) {
    setErro(null);
    setProgresso(0);

    try {
      await api.upload(`/transactions/${transactionId}/attachments`, arquivo, setProgresso);
      await queryClient.invalidateQueries({ queryKey: ['attachments', transactionId] });
    } catch (e) {
      // A recusa vem com o motivo ("o conteúdo é application/x-dosexec"). Trocar
      // por "erro ao enviar" faria tentar de novo com o mesmo arquivo.
      setErro(e instanceof ApiError ? e.message : 'Não foi possível enviar o arquivo.');
    } finally {
      setProgresso(null);
    }
  }

  if (transactionId === null) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 px-3.5 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
        <span className="font-medium">Anexos</span> — salve o lançamento primeiro para poder
        anexar nota fiscal, boleto ou comprovante.
      </div>
    );
  }

  const anexos = lista.data ?? [];

  return (
    <div className="rounded-lg border border-slate-200 p-3.5 dark:border-slate-800">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Anexos{anexos.length > 0 && ` (${anexos.length})`}
        </span>

        <label className="cursor-pointer rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
          {progresso === null ? 'Anexar arquivo' : `Enviando… ${progresso}%`}
          <input
            type="file"
            className="hidden"
            disabled={progresso !== null}
            accept=".pdf,.jpg,.jpeg,.png,.webp,.xml,.csv,.txt,.xlsx,.docx,.ods,.odt"
            onChange={(e) => {
              const arquivo = e.target.files?.[0];
              // Limpa o input: sem isto, escolher o MESMO arquivo de novo depois
              // de um erro não dispara `change` e o botão parece morto.
              e.target.value = '';
              if (arquivo) void enviar(arquivo);
            }}
          />
        </label>
      </div>

      {erro && (
        <p
          role="alert"
          className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
        >
          {erro}
        </p>
      )}

      {anexos.length === 0 && !lista.isPending && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Nenhum documento anexado. Aceita PDF, imagem, XML de NF-e e planilha, até 25 MB.
        </p>
      )}

      <ul className="mt-2 space-y-1.5">
        {anexos.map((a) => (
          <li
            key={a.id}
            className="flex items-center gap-2 rounded-md bg-slate-50 px-2.5 py-2 text-sm dark:bg-slate-800/50"
          >
            <span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              {a.extension}
            </span>

            <span className="min-w-0 flex-1 truncate" title={a.original_name}>
              {a.original_name}
              <span className="ml-1.5 text-xs text-slate-500 dark:text-slate-400">
                {a.size_human}
              </span>
            </span>

            {/* A ficha pode sobreviver ao arquivo: restaurar só o backup do
                banco deixa exatamente este estado. Melhor dizer do que deixar o
                download falhar sem explicação. */}
            {!a.available ? (
              <span className="shrink-0 text-xs font-medium text-rose-600 dark:text-rose-400">
                arquivo ausente
              </span>
            ) : (
              <button
                type="button"
                onClick={() => void api.download(`/attachments/${a.id}/download`, { entityIds })}
                className="shrink-0 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
              >
                Baixar
              </button>
            )}

            <button
              type="button"
              disabled={excluir.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Excluir "${a.original_name}"?\n\n` +
                      'O arquivo sai do servidor e não há como desfazer. ' +
                      'Se houver um backup dos anexos, ele ainda estará lá.',
                  )
                ) {
                  excluir.mutate(a.id);
                }
              }}
              className="shrink-0 text-xs font-medium text-rose-600 hover:underline disabled:opacity-50 dark:text-rose-400"
            >
              Excluir
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
